import { useEffect, useState } from "react"
import PubSub from "pubsub-js"
import Button from "aldoo-ra/Button"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Checkbox from "aldoo-ra/Checkbox"
import { getConsent, isConsentRequired, setConsent } from "./consent"

/** Asking for the banner again (a "Cookie settings" link): PubSub.publish(CONSENT_OPEN). */
export const CONSENT_OPEN = "Tracking_ConsentOpen"

/**
 * The cookie banner of Google consent mode: shown where consent is asked
 * (window.__consentRequired, set by the head from the visitor's country and
 * the page's TRACKING_CONSENT) to a visitor who has not chosen yet, and only
 * when the page carries a tag. "Accept" grants analytics and ads storage,
 * "Reject" keeps them denied (tags still send cookieless pings, from which
 * Google models conversions). The choice is kept (Tracking/consent).
 *
 * labels: { title, text, shortText, ok, enable, policy, policyHref, logo, company }
 *   (the older two buttons' accept / reject still name OK and the toggle when ok / enable are not given)
 *
 * One choice and one button: the "allow cookies" toggle (on to start with) and OK, which keeps the
 * choice — granted with the toggle on, denied with it off. On a desktop it is a bar across the
 * bottom: the company's logo and name on the left, the title and text in the middle, a wide OK on
 * the right, and the toggle under them on the left. On a phone: the short text with OK beside it,
 * and the toggle as a checkbox below.
 *
 * It sits above a bar the page fixes to the bottom of the screen (an element marked
 * data-fixed-bar, e.g. a product's buy bar), so it never covers it.
 */
export default function ConsentBanner({ labels, className = "" }) {
  const [open, setOpen] = useState(false)
  const [lift, setLift] = useState(0)
  // what OK keeps: the toggle starts on; a visitor who has chosen before sees their choice
  const [allowed, setAllowed] = useState(true)

  // the height of a visible bar fixed to the bottom of the screen: the banner rides above it
  useEffect(() => {
    if (!open) return
    const measure = () => {
      const bar = [...document.querySelectorAll("[data-fixed-bar]")].find((el) => {
        const style = window.getComputedStyle(el)
        const rect = el.getBoundingClientRect()
        return style.visibility !== "hidden" && Number(style.opacity) > 0.5 && Math.abs(rect.bottom - window.innerHeight) < 2
      })
      setLift(bar ? Math.round(bar.getBoundingClientRect().height) : 0)
    }
    measure()
    const timer = setInterval(measure, 500)
    window.addEventListener("resize", measure)
    return () => {
      clearInterval(timer)
      window.removeEventListener("resize", measure)
    }
  }, [open])

  useEffect(() => {
    const tagged = !!(window.__aldooTracking || window.__aldooGTM)
    if (tagged && isConsentRequired() && !getConsent()) setOpen(true)
    const token = PubSub.subscribe(CONSENT_OPEN, () => {
      const chosen = getConsent()
      if (chosen) setAllowed(chosen === "granted")
      setOpen(true)
    })
    return () => PubSub.unsubscribe(token)
  }, [])

  if (!open) return null
  const save = () => {
    setConsent(allowed ? "granted" : "denied")
    setOpen(false)
  }
  const ok = labels.ok || labels.accept || "OK"
  const enable = labels.enable || "Allow cookies"
  const policy = labels.policyHref && (
    <a href={labels.policyHref} className="underline text-primary whitespace-nowrap">
      {labels.policy}
    </a>
  )
  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={labels.title || labels.text}
      className={`fixed inset-x-0 bottom-0 z-[1100] pointer-events-none transition-[bottom] duration-200 ${className}`}
      style={{ bottom: lift }}
      data-testid="consent-banner"
    >
      <div className="pointer-events-auto bg-paper border-t border-bordered shadow-[0_-8px_30px_rgba(0,0,0,0.12)]">
        {/* desktop: logo | title + text | OK, the toggle below on the left */}
        <div className="hidden md:block mx-auto max-w-[1280px] px-8 pt-6 pb-5">
          <div className="flex items-start gap-8">
            <div className="w-[160px] shrink-0 flex flex-col items-center gap-1 pt-1">
              {labels.logo && <img src={labels.logo} alt={labels.company || ""} className="max-w-[140px] max-h-[40px] object-contain" />}
              {labels.company && <span className="text-[12px] font-semibold text-text_secondary">{labels.company}</span>}
            </div>
            <div className="flex-1 min-w-0">
              {labels.title && <p className="m-0 mb-2 text-[15px] font-bold text-text">{labels.title}</p>}
              <p className="m-0 text-[14px] leading-relaxed text-text">
                {labels.text} {policy}
              </p>
            </div>
            <Button variant="primary_l" onClick={save} className="shrink-0 w-[260px]" data-testid="consent-accept">
              {ok}
            </Button>
          </div>
          <div className="mt-4 pt-4 border-t border-bordered flex items-center gap-3">
            <ToggleSwitch isOn={allowed} onChange={setAllowed} />
            <span className="text-[14px] font-semibold text-text cursor-pointer select-none" onClick={() => setAllowed(!allowed)} data-testid="consent-toggle">
              {enable}
            </span>
          </div>
        </div>
        {/* phone: the short text with OK beside it, the checkbox below */}
        <div className="md:hidden px-3 py-3 flex flex-col gap-2.5">
          <div className="flex items-center gap-3">
            <p className="m-0 flex-1 min-w-0 text-[12px] leading-snug text-text">
              {labels.shortText || labels.text} {policy}
            </p>
            <Button variant="primary_s" onClick={save} className="shrink-0 min-w-[88px]" data-testid="consent-accept-mobile">
              {ok}
            </Button>
          </div>
          <Checkbox isOn={allowed} onChange={setAllowed} label={enable} labelClassName="text-[12px] text-text" />
        </div>
      </div>
    </div>
  )
}
